import { useState, useEffect, useCallback } from 'react';
import { useIonRouter } from '@ionic/react';
import { DataTable } from '../../components/shared/DataTable';
import { CourseModal } from '../../components/content/CourseModal';
import { CourseVideosModal } from '../../components/content/CourseVideosModal';
import { GameManagerModal } from '../../components/content/GameManagerModal';
import { ConfirmDeleteModal } from '../../components/content/ConfirmDeleteModal';
import { searchCourses, deleteCourse } from '../../services/content/courseService';
import { useRefresh } from '../../contexts/RefreshContext';
import type { Course } from '../../types/content/course';
import type { Column } from '../../components/shared/DataTable';

const columns: Column<Course>[] = [
  {
    key: 'status',
    label: 'Status',
    render: (item) => (
      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
        item.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
      }`}>
        {item.status}
      </span>
    ),
  },
  { key: 'name', label: 'Name' },
  // Category / Category Order are no longer persisted on Course. A course can
  // belong to different categories in different course groups; see the
  // CourseGroup editor for per-group assignment.
  {
    key: 'steps',
    label: 'Steps',
    render: (item) => String(item.steps),
  },
  {
    key: 'imageUrl',
    label: 'Image',
    render: (item) => item.imageUrl ? (
      <img src={item.imageUrl} alt={item.name} className="w-10 h-6 object-cover rounded" />
    ) : '—',
  },
];

function RowMenu({ onViewDetails, onEdit, onVideos, onGame, onDelete }: { onViewDetails: () => void; onEdit: () => void; onVideos: () => void; onGame: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
          <button onClick={() => { setOpen(false); onViewDetails(); }} className="w-full text-left px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50">View Details</button>
          <button onClick={() => { setOpen(false); onEdit(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Edit</button>
          <button onClick={() => { setOpen(false); onVideos(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Videos</button>
          <button onClick={() => { setOpen(false); onGame(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Game</button>
          <button onClick={() => { setOpen(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">Delete</button>
        </div>
      )}
    </div>
  );
}

export default function CoursesTab() {
  const router = useIonRouter();
  const [data, setData] = useState<Course[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalCourse, setModalCourse] = useState<Course | undefined>(undefined);
  const [videoCourse, setVideoCourse] = useState<Course | null>(null);
  const [gameCourseId, setGameCourseId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const results = await searchCourses({}, { page, pageSize, orderBy: 'name', order: 'asc' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/content/courses', fetch);
    return () => unregisterRefreshHandler('/content/courses');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  async function handleDelete(id: string) {
    await deleteCourse(id);
    setPendingDeleteId(null);
    fetch();
  }

  return (
    <>
      <DataTable
        title="Courses"
        columns={columns}
        data={data}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        onRefresh={fetch}
        onAdd={() => { setModalCourse(undefined); setShowModal(true); }}
        alignTop
        rowMenu={(item) => (
          <RowMenu
            onViewDetails={() => router.push(`/content/course-detail?courseId=${item.id}`, 'forward')}
            onEdit={() => { setModalCourse(item); setShowModal(true); }}
            onVideos={() => setVideoCourse(item)}
            onGame={() => setGameCourseId(item.id)}
            onDelete={() => setPendingDeleteId(item.id)}
          />
        )}
      />
      {showModal && (
        <CourseModal
          course={modalCourse}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); fetch(); }}
        />
      )}
      {videoCourse && (
        <CourseVideosModal
          course={videoCourse}
          onClose={() => setVideoCourse(null)}
          onSuccess={() => { setVideoCourse(null); fetch(); }}
        />
      )}
      {pendingDeleteId && (
        <ConfirmDeleteModal title="Delete Course" message="Are you sure you want to delete this course?" onCancel={() => setPendingDeleteId(null)} onConfirm={() => handleDelete(pendingDeleteId)} />
      )}
      {gameCourseId && (
        <GameManagerModal
          courseId={gameCourseId}
          onClose={() => setGameCourseId(null)}
        />
      )}
    </>
  );
}
